/**
 * StreamingPlaceholder — Skeleton loading with animated lane progress bars
 *
 * Shows lane racing: 🔍 Search, 📋 JSON, 🐍 Python progress.
 */
import React from 'react';
import type { StreamingPlaceholderData } from '../types';

const DEFAULT_LANES: StreamingPlaceholderData['lanes'] = [
  { id: 'search', label: 'Search', emoji: '🔍', progress: 0, status: 'waiting' },
  { id: 'json', label: 'JSON', emoji: '📋', progress: 0, status: 'waiting' },
  { id: 'python', label: 'Python', emoji: '🐍', progress: 0, status: 'waiting' },
];

interface StreamingPlaceholderProps {
  data?: StreamingPlaceholderData;
}

export const StreamingPlaceholder: React.FC<StreamingPlaceholderProps> = ({ data }) => {
  const lanes = data?.lanes ?? DEFAULT_LANES;
  const message = data?.message ?? 'Building your answer...';

  return (
    <div className="rounded-2xl bg-black/40 border border-cyan-400/20 p-5 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-5 h-5 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
        <span className="text-sm font-semibold text-cyan-300">{message}</span>
      </div>

      {/* Lane progress bars */}
      <div className="space-y-3">
        {lanes.map((lane) => {
          const isComplete = lane.status === 'complete';
          const isRunning = lane.status === 'running';

          return (
            <div key={lane.id} className="flex items-center gap-3">
              {/* Emoji + label */}
              <div className="flex items-center gap-2 w-24 shrink-0">
                <span className="text-sm">{lane.emoji}</span>
                <span className={`text-xs font-medium ${isComplete ? 'text-cyan-300' : 'text-cyan-400/60'}`}>
                  {lane.label}
                </span>
              </div>

              {/* Progress bar */}
              <div className="flex-1 h-2 rounded-full bg-cyan-400/10 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    isComplete
                      ? 'bg-cyan-400/70'
                      : isRunning
                        ? 'bg-cyan-400/50 animate-pulse'
                        : 'bg-cyan-400/20'
                  }`}
                  style={{ width: `${lane.progress}%` }}
                />
              </div>

              {/* Status */}
              <span className="w-10 text-right text-xs text-cyan-400/50">
                {isComplete ? '✓' : `${lane.progress}%`}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom skeleton blocks */}
      <div className="mt-5 space-y-3">
        <div className="h-4 w-3/4 rounded bg-cyan-400/5 animate-pulse" />
        <div className="h-4 w-1/2 rounded bg-cyan-400/5 animate-pulse" />
        <div className="h-4 w-5/6 rounded bg-cyan-400/5 animate-pulse" />
      </div>
    </div>
  );
};

export default StreamingPlaceholder;
